import React, { useRef, useEffect, useLayoutEffect } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  baseOpacity: number;
  color: string;
}

const COLORS_DEFAULT = [
  'rgba(146, 103, 253, 1)',  // Violeta claro brillante (#9267FD) - Principal
  'rgba(0, 105, 224, 1)',    // Azul eléctrico (#0069E0)
  'rgba(133, 190, 255, 1)',  // Azul cielo (#85BEFF)
  'rgba(100, 150, 255, 1)',  // Azul violeta (#6496FF)
  'rgba(0, 200, 255, 1)',    // Cian brillante (#00C8FF)
  'rgba(150, 100, 255, 1)',  // Violeta azulado (#9664FF)
];

const COLORS_WHITE = [
  'rgba(255, 255, 255, 1)',
  'rgba(255, 255, 255, 1)',
  'rgba(255, 255, 255, 1)',
  'rgba(255, 255, 255, 1)',
  'rgba(255, 255, 255, 1)',
  'rgba(255, 255, 255, 1)',
];

const COLORS_VIOLET = [
  'rgba(93, 63, 211, 1)',   // #5D3FD3
  'rgba(74, 48, 184, 1)',   // #4a30b8
  'rgba(146, 103, 253, 1)', // #9267FD
  'rgba(124, 58, 237, 1)',  // #7c3aed
  'rgba(139, 92, 246, 1)',  // #8b5cf6
  'rgba(167, 139, 250, 1)', // #a78bfa
];

interface BackgroundParticlesProps {
  intensity?: number;
  particleCount?: number;
  highActivity?: boolean;
  disableAnimation?: boolean;
  /** 'default' | 'white' (tema violeta) | 'violet' (tema violet-inverted) */
  colorSet?: 'default' | 'white' | 'violet';
}

function getColors(colorSet: 'default' | 'white' | 'violet') {
  if (colorSet === 'white') return COLORS_WHITE;
  if (colorSet === 'violet') return COLORS_VIOLET;
  return COLORS_DEFAULT;
}

export function BackgroundParticles({
  intensity = 0.2,
  particleCount = 50,
  highActivity = false,
  disableAnimation = false,
  colorSet = 'default',
}: BackgroundParticlesProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animationRef = useRef<number | null>(null);
  const dimensionsRef = useRef({ w: 0, h: 0 });
  const previousConnectionsRef = useRef<Set<string>>(new Set());
  const connectionPulseRef = useRef<Map<string, number>>(new Map());

  // Detect Lighthouse to auto-disable heavy canvas animations
  const isLighthouse = typeof navigator !== 'undefined' && /Lighthouse/i.test(navigator.userAgent);
  const shouldDisable = disableAnimation || isLighthouse;

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !canvas.parentElement) return;
    const parent = canvas.parentElement;
    const w = parent.clientWidth;
    const h = parent.clientHeight;
    canvas.width = w;
    canvas.height = h;
    dimensionsRef.current = { w, h };

    if (shouldDisable) return;

    const colors = getColors(colorSet);
    particlesRef.current = [];
    for (let i = 0; i < particleCount; i++) {
      particlesRef.current.push({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.2,
        vy: (Math.random() - 0.5) * 0.2,
        size: Math.random() * 1 + 0.5,
        baseOpacity: Math.random() * 0.15 + 0.05,
        color: colors[Math.floor(Math.random() * colors.length)],
      });
    }
  }, [particleCount, shouldDisable, colorSet]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || shouldDisable) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const CONNECTION_DISTANCE = 110; // Distancia máxima para conectar partículas
    const CONNECTION_DISTANCE_SQ = CONNECTION_DISTANCE * CONNECTION_DISTANCE;
    const MAX_CONNECTIONS = 2; // Máximo de conexiones por partícula
    const PULSE_DURATION = 30; // Duración del pulso de brillo en frames

    // Aumentar velocidad cuando hay alta actividad
    const speedMultiplier = highActivity ? 1.5 : 1.0; // 50% más rápido cuando está activo

    const getConnectionKey = (i: number, j: number) => `${Math.min(i, j)}-${Math.max(i, j)}`;

    const animate = () => {
      const { w, h } = dimensionsRef.current;
      ctx.clearRect(0, 0, w, h);
      const particles = particlesRef.current;
      const currentConnections = new Set<string>();

      // Actualizar posición de partículas (con velocidad aumentada si está activo)
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.x += p.vx * speedMultiplier;
        p.y += p.vy * speedMultiplier;
        if (p.x < 0 || p.x > w) p.vx *= -1;
        if (p.y < 0 || p.y > h) p.vy *= -1;
        p.x = Math.max(0, Math.min(w, p.x));
        p.y = Math.max(0, Math.min(h, p.y));
      }

      // Detectar conexiones actuales y cambios
      for (let i = 0; i < particles.length; i++) {
        const p1 = particles[i];
        let connections = 0;
        for (let j = i + 1; j < particles.length; j++) {
          if (connections >= MAX_CONNECTIONS) break;

          const p2 = particles[j];
          const dx = p2.x - p1.x;
          const dy = p2.y - p1.y;
          const distSq = dx * dx + dy * dy;

          if (distSq < CONNECTION_DISTANCE_SQ) {
            const connectionKey = getConnectionKey(i, j);
            currentConnections.add(connectionKey);

            // Detectar si es una nueva conexión o se rompió
            if (!previousConnectionsRef.current.has(connectionKey)) {
              // Nueva conexión - iniciar pulso
              connectionPulseRef.current.set(connectionKey, PULSE_DURATION);
            }

            connections++;
          }
        }
      }

      // Detectar conexiones rotas
      previousConnectionsRef.current.forEach((key) => {
        if (!currentConnections.has(key)) {
          // Conexión rota - iniciar pulso de desvanecimiento
          connectionPulseRef.current.set(key, PULSE_DURATION);
        }
      });

      // Actualizar y reducir pulsos
      connectionPulseRef.current.forEach((pulse, key) => {
        if (!currentConnections.has(key) && !previousConnectionsRef.current.has(key)) {
          connectionPulseRef.current.delete(key);
        } else {
          connectionPulseRef.current.set(key, Math.max(0, pulse - 1));
        }
      });

      // Dibujar conexiones (aristas de la red neuronal) - Con pulso de brillo
      for (let i = 0; i < particles.length; i++) {
        const p1 = particles[i];
        let connections = 0;
        for (let j = i + 1; j < particles.length; j++) {
          if (connections >= MAX_CONNECTIONS) break;

          const p2 = particles[j];
          const dx = p2.x - p1.x;
          const dy = p2.y - p1.y;
          const distSq = dx * dx + dy * dy;

          if (distSq < CONNECTION_DISTANCE_SQ) {
            const distance = Math.sqrt(distSq);
            const connectionKey = getConnectionKey(i, j);
            const pulse = connectionPulseRef.current.get(connectionKey) || 0;
            const pulseIntensity = pulse / PULSE_DURATION; // 1.0 cuando es nuevo, 0.0 cuando se desvanece

            // Aumentar brillo durante el pulso
            const baseOpacity = (1 - distance / CONNECTION_DISTANCE) * intensity * 1.5;
            const pulseBoost = pulseIntensity * 3.5; // Aumento de brillo durante el pulso
            const opacity = Math.min(1, baseOpacity * (1 + pulseBoost));

            const gradient = ctx.createLinearGradient(p1.x, p1.y, p2.x, p2.y);

            // Gradiente brillante para las conexiones - Con pulso (solo colores fríos)
            const color1 = p1.color.replace('1', Math.min(1, opacity * 1.5).toString());
            const color2 = `rgba(146, 103, 253, ${Math.min(1, opacity * 1.3)})`;
            const color3 = p2.color.replace('1', Math.min(1, opacity * 1.5).toString());
            gradient.addColorStop(0, color1);
            gradient.addColorStop(0.5, color2);
            gradient.addColorStop(1, color3);

            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = gradient;
            ctx.lineWidth = 1.0 + (pulseIntensity * 0.8); // Línea más fina
            ctx.shadowBlur = (20 + pulseIntensity * 30) * intensity; // Más brillo
            // Sombra solo en colores fríos (azul/violeta/cian)
            ctx.shadowColor = `rgba(133, 190, 255, ${Math.min(1, opacity * (1.5 + pulseBoost))})`;
            ctx.stroke();
            ctx.shadowBlur = 0;
            connections++;
          }
        }
      }

      // Actualizar conexiones anteriores
      previousConnectionsRef.current = currentConnections;

      // Dibujar partículas (nodos de la red) - Con más brillo (solo colores fríos)
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        const opacity = p.baseOpacity * intensity * 1.5;

        // Convertir color a rgba con opacidad correcta (evitar tonos café)
        const colorMatch = p.color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
        if (!colorMatch) continue;
        const r = parseInt(colorMatch[1]);
        const g = parseInt(colorMatch[2]);
        const b = parseInt(colorMatch[3]);

        // Asegurar que solo usamos colores fríos (azules/violetas/cianes)
        // Si el color tiene mucho rojo, lo convertimos a azul/violeta
        const adjustedR = Math.min(r, 150); // Limitar rojo para evitar tonos cálidos
        const adjustedG = Math.min(g, 200);
        const adjustedB = Math.max(b, 200); // Asegurar azul brillante

        // Halo exterior brillante - Más pequeño
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * 2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${adjustedR}, ${adjustedG}, ${adjustedB}, ${Math.min(1, opacity * 0.5)})`;
        ctx.shadowBlur = 20 * intensity;
        ctx.shadowColor = `rgba(133, 190, 255, ${Math.min(1, opacity * 1.5)})`; // Azul cielo
        ctx.fill();

        // Halo medio brillante - Más pequeño
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * 1.5, 0, Math.PI * 2);  // Reducido de *2 a *1.5
        ctx.fillStyle = `rgba(${adjustedR}, ${adjustedG}, ${adjustedB}, ${Math.min(1, opacity * 0.6)})`;
        ctx.shadowBlur = 12 * intensity;  // Reducido de 15 a 12
        ctx.shadowColor = `rgba(146, 103, 253, ${Math.min(1, opacity * 1.0)})`; // Violeta para sombra
        ctx.fill();

        // Nodo principal - Más pequeño y brillante
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${adjustedR}, ${adjustedG}, ${adjustedB}, ${Math.min(1, opacity * 1.2)})`;
        ctx.shadowBlur = 6 * intensity;  // Reducido de 8 a 6
        ctx.shadowColor = `rgba(0, 200, 255, ${Math.min(1, opacity * 0.9)})`; // Cian para sombra
        ctx.fill();
        ctx.shadowBlur = 0;
      }

      animationRef.current = requestAnimationFrame(animate);
    };
    animate();
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [intensity, highActivity]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas?.parentElement) return;
    const ro = new ResizeObserver(() => {
      const parent = canvas.parentElement!;
      const w = parent.clientWidth;
      const h = parent.clientHeight;
      if (w === 0 || h === 0) return; // Ignorar si el contenedor no tiene tamaño
      const oldW = dimensionsRef.current.w || w;
      const oldH = dimensionsRef.current.h || h;
      canvas.width = w;
      canvas.height = h;
      // Escalar posiciones proporcionalmente al nuevo tamaño
      const scaleX = oldW > 0 ? w / oldW : 1;
      const scaleY = oldH > 0 ? h / oldH : 1;
      particlesRef.current = particlesRef.current.map((p) => ({
        ...p,
        x: Math.max(0, Math.min(p.x * scaleX, w)),
        y: Math.max(0, Math.min(p.y * scaleY, h)),
      }));
      dimensionsRef.current = { w, h };
    });
    ro.observe(canvas.parentElement);
    return () => ro.disconnect();
  }, []);

  return React.createElement('canvas', {
    ref: canvasRef,
    className: 'background-particles-canvas',
    style: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      width: '100%',
      height: '100%',
      zIndex: 1,
      pointerEvents: 'none',
    }
  });
}

// No exportar default para evitar conflictos
// export default BackgroundParticles;
